import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  InputErrorMessage,
  Label,
  Progress,
  SingleFileUploader,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { LegacyDocumentType } from '@prisma/client';
import { useEffect, useState } from 'react';
import { useForm } from '../../../../../../hooks/useZodForm';
import { documentInfoSchema, TDocumentInfoFields } from './schema';

type TDocumentInfoFormProps = {
  onNext: (formData: TDocumentInfoFields) => void;
  onBack: () => void;
  director?: DirectorBase | null;
  companyId: number;
};

export const DocumentInfoForm = ({
  onNext,
  onBack,
  director,
  companyId,
}: TDocumentInfoFormProps) => {
  const selectedEmiratesId = director?.directorFiles
    ?.sort((a, b) => b.id - a.id)
    .find(
      (eachFile) => eachFile.documentType === LegacyDocumentType.EmiratesId
    );

  const selectedVisa = director?.directorFiles
    ?.sort((a, b) => b.id - a.id)
    .find((eachFile) => eachFile.documentType === LegacyDocumentType.Visa);

  const [residenceVisa, setResidenceVisa] = useState<FileBasic>(selectedVisa);
  const [emiratesId, setEmiratesId] = useState<FileBasic>(selectedEmiratesId);
  const [addressProof, setAddressProof] = useState<FileBasic>(
    director?.addressProof
  );

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    setError,
  } = useForm<TDocumentInfoFields>({
    resolver: zodResolver(documentInfoSchema),
  });

  const onSubmit = (formData: TDocumentInfoFields) => {
    onNext(formData);
  };

  useEffect(() => {
    if (!residenceVisa) setValue('residenceVisa', null);
    else {
      setValue('residenceVisa', residenceVisa);
      clearErrors('residenceVisa');
    }
  }, [residenceVisa]);

  useEffect(() => {
    if (!emiratesId) setValue('emiratesId', null);
    else {
      setValue('emiratesId', emiratesId);
      clearErrors('emiratesId');
    }
  }, [emiratesId]);

  useEffect(() => {
    if (!addressProof) setValue('addressProof', null);
    else {
      setValue('addressProof', addressProof);
      clearErrors('addressProof');
    }
  }, [addressProof]);

  return (
    <div className="max-h-[85vh] overflow-hidden flex flex-col">
      <DialogHeader className="px-6 py-5 border-none">
        <DialogTitle>Passport Info</DialogTitle>
      </DialogHeader>
      <div className="flex gap-2">
        <Progress className="rounded-full h-1" accent="success" value={100} />
        <Progress className="rounded-full h-1" accent="success" value={100} />
        <Progress className="rounded-full h-1" accent="default" value={100} />
      </div>
      <div className="grow overflow-hidden">
        <div className="p-6 flex flex-col gap-4 h-full overflow-y-auto">
          <FieldWrapper>
            <Label>
              Residence Visa
              <span className="text-violet-solid ml-1">*</span>
            </Label>
            <SingleFileUploader
              companyId={companyId}
              setFile={setResidenceVisa}
              file={residenceVisa}
              error={errors?.residenceVisa?.message}
              setError={(msg: string) =>
                setError('residenceVisa', { type: 'custom', message: msg })
              }
              token={undefined}
              accept={{
                'application/pdf': ['.pdf'],
                'image/*': ['.png', '.jpg', '.jpeg'],
              }}
              maxSize={10 * 1024 * 1024}
              documentType={LegacyDocumentType.Passport}
              _className="w-full"
            />
            {errors?.residenceVisa?.message ? (
              <InputErrorMessage message="Please upload your residence visa." />
            ) : null}
          </FieldWrapper>
          <FieldWrapper>
            <Label>
              Emirates ID
              <span className="text-violet-solid ml-1">*</span>
            </Label>
            <SingleFileUploader
              companyId={companyId}
              setFile={setEmiratesId}
              file={emiratesId}
              error={errors?.emiratesId?.message}
              setError={(msg: string) =>
                setError('emiratesId', { type: 'custom', message: msg })
              }
              token={undefined}
              accept={{
                'application/pdf': ['.pdf'],
                'image/*': ['.png', '.jpg', '.jpeg'],
              }}
              maxSize={10 * 1024 * 1024}
              documentType={LegacyDocumentType.Passport}
              _className="w-full"
            />
            {errors?.emiratesId?.message ? (
              <InputErrorMessage message="Please upload your emirates id." />
            ) : null}
          </FieldWrapper>
          <FieldWrapper>
            <Label>
              Proof of address
              <span className="text-violet-solid ml-1">*</span>
            </Label>
            <SingleFileUploader
              companyId={companyId}
              setFile={setAddressProof}
              file={addressProof}
              error={errors?.addressProof?.message}
              setError={(msg: string) =>
                setError('addressProof', { type: 'custom', message: msg })
              }
              token={undefined}
              accept={{
                'application/pdf': ['.pdf'],
                'image/*': ['.png', '.jpg', '.jpeg'],
              }}
              maxSize={10 * 1024 * 1024}
              documentType={LegacyDocumentType.Passport}
              _className="w-full"
            />
            {errors?.addressProof?.message ? (
              <InputErrorMessage message="Please upload your address proof." />
            ) : null}
          </FieldWrapper>
        </div>
      </div>
      <DialogFooter>
        <Button size="sm" variant="secondary" onClick={onBack}>
          Back
        </Button>
        <Button size="sm" onClick={handleSubmit(onSubmit)}>
          Next
        </Button>
      </DialogFooter>
    </div>
  );
};
